import { createBrowserHistory as createHistory } from 'history'
import React, { Component } from 'react'
import { Provider as StoreProvider } from 'react-redux'
import { Router } from 'react-router-dom'

import { Provider as InjectProvider } from 'modules/di'
import { HttpClient } from 'modules/http-client'
import { configureModuleLoader } from 'modules/module-loader'
import { WebStorage } from 'modules/web-storage'

import { API_VERSION, ENDPOINT, IS_DEV } from '../environment'

import { App } from './app'
import { AuthModule, Providers as AuthProviders } from './auth/auth.module'
import { createEpics, createI18n, createStore } from './core'
import { CoreModule, Providers as CoreProviders } from './core/core.module'

import { fromMultiselect, fromSuggest } from 'modules/forms'
import { fromSignalR } from 'modules/signal-r'

export interface AppProviders extends CoreProviders, AuthProviders {
  httpClient: HttpClient
  webStorage: WebStorage
}
export type InjectedProviders<P extends keyof AppProviders> = Pick<AppProviders, P>

// create Singleton services
const httpClient = new HttpClient({ withCredentials: true, baseURL: `${ENDPOINT}/${API_VERSION}` })
const webStorage = new WebStorage('HCI-PMAN')

const i18nConfig = {
  debug: IS_DEV,
  backend: {
    loadPath: `${ENDPOINT}/v1/locale`,
    allowMultiLoading: false,
    crossDomain: true,
    withCredentials: true,
  },
}
// instantiate eagerly loaded modules
const coreModule = CoreModule.forRoot({ httpClient, history: createHistory(), i18n: createI18n(i18nConfig) })
const authModule = AuthModule.forRoot({ httpClient, routerService: coreModule.providers!.routerService, webStorage })
const modules = {
  core: coreModule,
  auth: authModule,
}

export const providers: AppProviders = {
  httpClient,
  webStorage,
  ...modules.core.providers!,
  ...modules.auth.providers!,
}

export const epicsModule = createEpics(
  [...modules.core.epics!, ...modules.auth.epics!, fromSuggest.epics, fromMultiselect.epics, fromSignalR.epics],
  providers
)
export const storeModule = createStore(
  {},
  [
    { id: modules.core.store!.id, reducers: modules.core.store!.reducers },
    { id: modules.auth.store!.id, reducers: modules.auth.store!.reducers },
    { id: fromSuggest.STORE_ID, reducers: fromSuggest.reducer },
    { id: fromMultiselect.STORE_ID, reducers: fromMultiselect.reducer },
    { id: fromSignalR.STORE_ID, reducers: fromSignalR.reducer },
  ],
  [epicsModule.middleware]
)

// providers.i18nService.configure({ emitFn: storeModule.dispatch })
configureModuleLoader({ logger: providers.logger, storeModule, epicsModule })

export class AppModule extends Component {
  render() {
    return (
      <StoreProvider store={storeModule}>
        <Router history={epicsModule.providers.routerService.provider}>
          <InjectProvider injectables={epicsModule.providers}>
            <App />
          </InjectProvider>
        </Router>
      </StoreProvider>
    )
  }
}



// WEBPACK FOOTER //
// ./src/app/app.module.tsx